<template>
  <section
    class="compensacao"
    :aria-labelledby="`${uid}-titulo`"
    data-testid="compensacao-horas-sindicato"
  >
    <header class="compensacao__header">
      <span class="compensacao__icone">
        <v-icon color="#5e72e4">mdi-scale-balance</v-icon>
      </span>
      <div>
        <span class="compensacao__eyebrow">
          {{ $t("Compensacao_horas_eyebrow") }}
        </span>
        <h3 :id="`${uid}-titulo`" class="compensacao__titulo">
          {{ $t("Compensacao_horas_titulo") }}
        </h3>
        <p class="compensacao__descricao">
          {{ $t("Compensacao_horas_descricao") }}
        </p>
      </div>
    </header>

    <div
      class="compensacao-modos"
      role="radiogroup"
      :aria-label="$t('Compensacao_modo')"
    >
      <button
        v-for="modo in modos"
        :key="modo.valor"
        type="button"
        role="radio"
        class="compensacao-modo"
        :class="{ 'compensacao-modo--ativo': modoAtual === modo.valor }"
        :aria-checked="String(modoAtual === modo.valor)"
        @click="selecionarModo(modo.valor)"
      >
        <v-icon
          size="20"
          :color="modoAtual === modo.valor ? '#5e72e4' : '#98a2b3'"
        >
          {{ modo.icone }}
        </v-icon>
        <span class="compensacao-modo__texto">
          <strong>{{ $t(`Compensacao_modo_${modo.valor}`) }}</strong>
          <small>{{ $t(`Compensacao_modo_${modo.valor}_desc`) }}</small>
        </span>
        <v-icon
          v-if="modoAtual === modo.valor"
          size="18"
          color="#5e72e4"
          class="compensacao-modo__check"
        >
          mdi-check-circle
        </v-icon>
      </button>
    </div>

    <div
      v-if="compensacao90Dias && modoAtual !== modos[0].valor"
      class="compensacao-aviso compensacao-aviso--warning"
      role="status"
    >
      <v-icon small>mdi-alert-outline</v-icon>
      <span>
        {{
          $t("Compensacao_aviso_90_dias", {
            parametro: $t("APURACAOBANCODEHORASCOMPENSACAO90DIAS"),
          })
        }}
      </span>
    </div>

    <p v-if="modoAtual === modos[0].valor" class="compensacao__desativada">
      <v-icon small color="#667085">mdi-information-outline</v-icon>
      {{ $t("Compensacao_desativada_info") }}
    </p>

    <template v-else>
      <div class="compensacao-regra">
        <div
          v-for="coluna in colunas"
          :key="coluna.campo"
          class="compensacao-coluna"
          :class="`compensacao-coluna--${coluna.natureza}`"
        >
          <div class="compensacao-coluna__cabecalho">
            <v-icon size="18" :color="coluna.cor">{{ coluna.icone }}</v-icon>
            <div>
              <strong>{{ $t(coluna.titulo) }}</strong>
              <small>{{ $t(coluna.ajuda) }}</small>
            </div>
          </div>

          <ol class="compensacao-lista">
            <li
              v-for="(tipo, indice) in coluna.tipos"
              :key="tipo"
              class="compensacao-item"
              :class="{
                'compensacao-item--inativo':
                  tipo === 'ATRASO' && atrasoRemovido,
              }"
            >
              <span class="compensacao-item__ordem">{{ indice + 1 }}</span>
              <span class="compensacao-item__texto">
                <strong>{{ $t(tipo) }}</strong>
                <small>{{ $t(`Compensacao_tipo_${tipo}`) }}</small>
              </span>
              <span v-if="editavel" class="compensacao-item__acoes">
                <v-btn
                  icon
                  x-small
                  :disabled="indice === 0"
                  :aria-label="`${$t('Mover_para_cima')}: ${$t(tipo)}`"
                  :title="$t('Mover_para_cima')"
                  @click="mover(coluna.campo, indice, -1)"
                >
                  <v-icon small>mdi-arrow-up</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  :disabled="indice === coluna.tipos.length - 1"
                  :aria-label="`${$t('Mover_para_baixo')}: ${$t(tipo)}`"
                  :title="$t('Mover_para_baixo')"
                  @click="mover(coluna.campo, indice, 1)"
                >
                  <v-icon small>mdi-arrow-down</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  :aria-label="`${$t('Remover')}: ${$t(tipo)}`"
                  :title="$t('Remover')"
                  @click="remover(coluna.campo, tipo)"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </span>
            </li>
            <li v-if="!coluna.tipos.length" class="compensacao-lista__vazia">
              {{ $t(coluna.vazio) }}
            </li>
          </ol>

          <div
            v-if="editavel && coluna.disponiveis.length"
            class="compensacao-disponiveis"
          >
            <span>{{ $t("Compensacao_adicionar") }}</span>
            <button
              v-for="tipo in coluna.disponiveis"
              :key="tipo"
              type="button"
              class="compensacao-adicionar"
              @click="adicionar(coluna.campo, tipo)"
            >
              <v-icon x-small>mdi-plus</v-icon>
              {{ $t(tipo) }}
            </button>
          </div>
        </div>
      </div>

      <p class="compensacao__explicacao">
        <v-icon small color="#667085">mdi-information-outline</v-icon>
        {{ $t("Compensacao_regra_explicacao") }}
      </p>

      <div
        v-if="modoAtual === modos[1].valor"
        class="compensacao__personalizar"
      >
        <v-btn
          small
          outlined
          color="#5e72e4"
          class="text-none font-weight-bold"
          @click="selecionarModo(modos[2].valor)"
        >
          <v-icon left small>mdi-tune-variant</v-icon>
          {{ $t("Compensacao_personalizar_padrao") }}
        </v-btn>
      </div>

      <div
        v-if="atrasoRemovido && regra.debitos.includes('ATRASO')"
        class="compensacao-aviso compensacao-aviso--info"
        role="status"
      >
        <v-icon small>mdi-information-outline</v-icon>
        <span>
          {{
            $t("Compensacao_aviso_atraso", {
              parametro: $t("REMOVERCOMPENSACAOATRASO"),
            })
          }}
        </span>
      </div>
      <div
        v-if="editavel && (!regra.creditos.length || !regra.debitos.length)"
        class="compensacao-aviso compensacao-aviso--warning"
        role="status"
      >
        <v-icon small>mdi-alert-outline</v-icon>
        <span>
          {{ $t("Compensacao_aviso_sem_creditos_debitos") }}
        </span>
      </div>
      <div
        v-if="editavel && regra.creditos.includes('EXTRA100')"
        class="compensacao-aviso compensacao-aviso--info"
        role="status"
      >
        <v-icon small>mdi-information-outline</v-icon>
        <span>
          {{ $t("Compensacao_aviso_extra100") }}
          <template v-if="feriadoTrabalhado">
            {{ $t("Compensacao_aviso_feriado") }}
          </template>
        </span>
      </div>
    </template>

    <div class="compensacao-bloco">
      <button
        type="button"
        class="compensacao-bloco__toggle"
        :aria-expanded="String(simuladorAberto)"
        :aria-controls="`${uid}-simulador`"
        @click="simuladorAberto = !simuladorAberto"
      >
        <v-icon size="20" color="#5e72e4">mdi-flask-outline</v-icon>
        <span>
          <strong>{{ $t("Compensacao_simular") }}</strong>
          <small>{{ $t("Compensacao_simular_ajuda") }}</small>
        </span>
        <v-icon>
          {{ simuladorAberto ? "mdi-chevron-up" : "mdi-chevron-down" }}
        </v-icon>
      </button>

      <div
        v-if="simuladorAberto"
        :id="`${uid}-simulador`"
        class="compensacao-simulador"
      >
        <div class="compensacao-simulador__tabela">
          <table>
            <thead>
              <tr>
                <th scope="col">{{ $t("Tipo") }}</th>
                <th scope="col">{{ $t("Compensacao_apurado") }}</th>
                <th scope="col">{{ $t("Compensacao_compensado") }}</th>
                <th scope="col">{{ $t("Compensacao_resultado") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="linha in linhasSimulacao"
                :key="linha.tipo"
                :class="{ 'compensacao-linha--fora': !linha.participa }"
              >
                <th scope="row">
                  <span
                    class="compensacao-natureza"
                    :class="`compensacao-natureza--${linha.natureza}`"
                  ></span>
                  {{ $t(linha.tipo) }}
                  <small v-if="!linha.participa">
                    {{ $t("Compensacao_nao_participa") }}
                  </small>
                </th>
                <td>
                  <v-text-field
                    v-model="entradasSimulacao[linha.tipo]"
                    :aria-label="`${$t('Compensacao_apurado')}: ${$t(
                      linha.tipo
                    )}`"
                    :error="linha.invalido"
                    placeholder="00:00"
                    inputmode="decimal"
                    class="compensacao-hora"
                    dense
                    outlined
                    hide-details
                  ></v-text-field>
                </td>
                <td class="compensacao-simulador__compensado">
                  {{
                    linha.compensado
                      ? `− ${formatarHoras(linha.compensado)}`
                      : "—"
                  }}
                </td>
                <td>
                  <strong>{{ formatarHoras(linha.resultado) }}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ul class="compensacao-movimentos" aria-live="polite">
          <li v-for="(movimento, indice) in simulacao.movimentos" :key="indice">
            <v-icon x-small color="#12b76a">mdi-swap-horizontal</v-icon>
            {{
              $t("Compensacao_movimento", {
                horas: formatarHoras(movimento.horas),
                credito: $t(movimento.credito),
                debito: $t(movimento.debito),
              })
            }}
          </li>
          <li v-if="!simulacao.movimentos.length">
            {{ $t("Compensacao_sem_movimentos") }}
          </li>
        </ul>

        <v-btn
          x-small
          text
          color="#667085"
          class="text-none"
          @click="restaurarExemplo"
        >
          <v-icon left x-small>mdi-restore</v-icon>
          {{ $t("Compensacao_simulacao_restaurar") }}
        </v-btn>
      </div>
    </div>

    <div class="compensacao-bloco">
      <div class="compensacao-ajustes">
        <div class="compensacao-ajustes__grupo">
          <div class="compensacao-ajustes__cabecalho">
            <v-icon size="18" color="#5e72e4">mdi-autorenew</v-icon>
            <div>
              <strong>{{ $t("Compensacao_ajustes_automaticos") }}</strong>
              <small>{{ $t("Compensacao_ajustes_automaticos_ajuda") }}</small>
            </div>
          </div>
          <v-switch
            v-for="ajuste in ajustesAutomaticos"
            :key="ajuste.tipo"
            :input-value="ajuste.ativo"
            :label="$t(ajuste.tipo)"
            :hint="$t(`Compensacao_ajuste_${ajuste.tipo}`)"
            persistent-hint
            inset
            dense
            color="#5e72e4"
            class="compensacao-ajustes__switch"
            @change="definirParametrizacao(ajuste.tipo, $event === true)"
          ></v-switch>
        </div>

        <div class="compensacao-ajustes__grupo">
          <div class="compensacao-ajustes__cabecalho">
            <v-icon size="18" color="#5e72e4"
              >mdi-hand-back-right-outline</v-icon
            >
            <div>
              <strong>{{ $t("Compensacao_ajustes_manuais") }}</strong>
              <small>{{ $t("Compensacao_ajustes_manuais_ajuda") }}</small>
            </div>
          </div>
          <div class="compensacao-ajustes__chips">
            <span
              v-for="ajuste in ajustesManuais"
              :key="ajuste"
              class="compensacao-ajustes__chip"
            >
              {{ $t(ajuste) }}
            </span>
          </div>
          <small class="compensacao-ajustes__nota">
            {{ $t("Compensacao_ajustes_manuais_nota") }}
          </small>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  CREDITOS_COMPENSACAO,
  CREDITOS_PADRAO,
  DEBITOS_COMPENSACAO,
  DEBITOS_PADRAO,
  MODOS_COMPENSACAO,
  formatarHoras,
  modoCompensacao,
  parametrizacaoAtiva,
  parseHoras,
  regraCompensacao,
  simularCompensacao,
} from "@/utils/compensacaoHoras";

const EXEMPLO_SIMULACAO = {
  EXTRA50: "03:00",
  EXTRA60: "",
  EXTRA70: "",
  EXTRA100: "02:00",
  EXTRA50BANCOHORAS: "01:00",
  COMPENSAR: "02:30",
  ATRASO: "01:15",
};

const AJUSTES_AUTOMATICOS = [
  "COMPENSARFALTAINJUSTIFICADAEMFOLGASTRABALHADAS",
  "CONVERTERCOMPENSAREMATRASO",
  "REMOVERCOMPENSACAOATRASO",
];

const AJUSTES_MANUAIS = [
  "FOLGACOMPENSATORIA",
  "HORASCOMPENSATORIAS",
  "ABONARATRASO",
  "REDUCAOPROJECAO",
  "TROCAPLANTAO",
  "AJUSTESALDOBH",
  "Banco_de_horas_por_datas",
  "Validar_horas",
];

// Edita a regra de compensação diretamente no sindicato recebido (mesmo objeto salvo pelo formulário).
export default {
  name: "CompensacaoHorasSindicato",

  props: {
    recurso: { type: Object, required: true },
  },

  data() {
    return {
      uid: `compensacao-${Math.floor(Math.random() * 1000000)}`,
      simuladorAberto: false,
      entradasSimulacao: { ...EXEMPLO_SIMULACAO },
      ajustesManuais: AJUSTES_MANUAIS,
      modos: [
        { valor: MODOS_COMPENSACAO.DESATIVADA, icone: "mdi-cancel" },
        { valor: MODOS_COMPENSACAO.PADRAO, icone: "mdi-cog-outline" },
        { valor: MODOS_COMPENSACAO.PERSONALIZADA, icone: "mdi-tune-variant" },
      ],
    };
  },

  computed: {
    parametrizacoes() {
      return this.recurso.parametrizacoes || [];
    },
    modoAtual() {
      return modoCompensacao(this.recurso);
    },
    editavel() {
      return this.modoAtual === MODOS_COMPENSACAO.PERSONALIZADA;
    },
    regra() {
      return regraCompensacao(this.recurso);
    },
    compensacao90Dias() {
      return parametrizacaoAtiva(
        this.parametrizacoes,
        "APURACAOBANCODEHORASCOMPENSACAO90DIAS"
      );
    },
    atrasoRemovido() {
      return parametrizacaoAtiva(
        this.parametrizacoes,
        "REMOVERCOMPENSACAOATRASO"
      );
    },
    feriadoTrabalhado() {
      return parametrizacaoAtiva(this.parametrizacoes, "FERIADOTRABALHADO");
    },
    colunas() {
      return [
        {
          campo: "compensacaoCreditos",
          natureza: "credito",
          titulo: "Compensacao_creditos",
          ajuda: "Compensacao_creditos_ajuda",
          vazio: "Compensacao_sem_creditos",
          icone: "mdi-plus-circle-outline",
          cor: "#12b76a",
          tipos: this.regra.creditos,
          disponiveis: CREDITOS_COMPENSACAO.filter(
            (tipo) => !this.regra.creditos.includes(tipo)
          ),
        },
        {
          campo: "compensacaoDebitos",
          natureza: "debito",
          titulo: "Compensacao_debitos",
          ajuda: "Compensacao_debitos_ajuda",
          vazio: "Compensacao_sem_debitos",
          icone: "mdi-minus-circle-outline",
          cor: "#f04438",
          tipos: this.regra.debitos,
          disponiveis: DEBITOS_COMPENSACAO.filter(
            (tipo) => !this.regra.debitos.includes(tipo)
          ),
        },
      ];
    },
    compensacaoAplicada() {
      return (
        this.modoAtual !== MODOS_COMPENSACAO.DESATIVADA &&
        !this.compensacao90Dias
      );
    },
    saldosSimulacao() {
      const saldos = {};
      [...CREDITOS_COMPENSACAO, ...DEBITOS_COMPENSACAO].forEach((tipo) => {
        saldos[tipo] = parseHoras(this.entradasSimulacao[tipo]) || 0;
      });
      return saldos;
    },
    simulacao() {
      return simularCompensacao({
        creditos: this.compensacaoAplicada ? this.regra.creditos : [],
        debitos: this.compensacaoAplicada ? this.regra.debitos : [],
        saldos: this.saldosSimulacao,
        compensarAtraso: !this.atrasoRemovido,
      });
    },
    linhasSimulacao() {
      const participantes = this.compensacaoAplicada
        ? [
            ...this.regra.creditos,
            ...this.regra.debitos.filter(
              (tipo) => !(tipo === "ATRASO" && this.atrasoRemovido)
            ),
          ]
        : [];
      return [
        ...CREDITOS_COMPENSACAO.map((tipo) => ({ tipo, natureza: "credito" })),
        ...DEBITOS_COMPENSACAO.map((tipo) => ({ tipo, natureza: "debito" })),
      ].map((linha) => {
        const entrada = this.entradasSimulacao[linha.tipo];
        const apurado = this.saldosSimulacao[linha.tipo];
        const resultado = this.simulacao.saldos[linha.tipo];
        return {
          ...linha,
          participa: participantes.includes(linha.tipo),
          invalido:
            !!String(entrada || "").trim() && parseHoras(entrada) === null,
          compensado: apurado - resultado,
          resultado,
        };
      });
    },
    ajustesAutomaticos() {
      return AJUSTES_AUTOMATICOS.map((tipo) => ({
        tipo,
        ativo: parametrizacaoAtiva(this.parametrizacoes, tipo),
      }));
    },
  },

  methods: {
    formatarHoras,

    selecionarModo(modo) {
      if (modo === MODOS_COMPENSACAO.DESATIVADA) {
        this.definirParametrizacao("COMPENSARHORAS", false);
        return;
      }
      // Sem parametrização cadastrada a compensação já é ativa por padrão no cálculo.
      if (
        this.parametrizacoes.some(
          (item) => item && item.tipo === "COMPENSARHORAS"
        )
      ) {
        this.definirParametrizacao("COMPENSARHORAS", true);
      }
      const personalizada = modo === MODOS_COMPENSACAO.PERSONALIZADA;
      this.$set(this.recurso, "compensacaoPersonalizada", personalizada);
      if (
        personalizada &&
        !(this.recurso.compensacaoCreditos || []).length &&
        !(this.recurso.compensacaoDebitos || []).length
      ) {
        this.$set(this.recurso, "compensacaoCreditos", [...CREDITOS_PADRAO]);
        this.$set(this.recurso, "compensacaoDebitos", [...DEBITOS_PADRAO]);
      }
    },

    // Altera a lista no mesmo array usado pela tabela de parametrizações.
    definirParametrizacao(tipo, ativo) {
      if (!Array.isArray(this.recurso.parametrizacoes)) {
        this.$set(this.recurso, "parametrizacoes", []);
      }
      const existentes = this.recurso.parametrizacoes.filter(
        (item) => item && item.tipo === tipo
      );
      if (existentes.length) {
        existentes.forEach((item) => this.$set(item, "ativo", ativo));
      } else {
        this.recurso.parametrizacoes.push({ tipo, ativo, referencia: null });
      }
    },

    adicionar(campo, tipo) {
      const lista = this.listaEditavel(campo);
      this.$set(this.recurso, campo, [...lista, tipo]);
    },

    remover(campo, tipo) {
      const lista = this.listaEditavel(campo);
      this.$set(
        this.recurso,
        campo,
        lista.filter((item) => item !== tipo)
      );
    },

    mover(campo, indice, direcao) {
      const lista = [...this.listaEditavel(campo)];
      const destino = indice + direcao;
      if (destino < 0 || destino >= lista.length) {
        return;
      }
      [lista[indice], lista[destino]] = [lista[destino], lista[indice]];
      this.$set(this.recurso, campo, lista);
    },

    listaEditavel(campo) {
      return campo === "compensacaoCreditos"
        ? this.regra.creditos
        : this.regra.debitos;
    },

    restaurarExemplo() {
      this.entradasSimulacao = { ...EXEMPLO_SIMULACAO };
    },
  },
};
</script>

<style scoped>
/* O tema não exibe o contorno dos campos "outlined"; reforça que são editáveis */
.compensacao ::v-deep .v-text-field--outlined .v-input__slot {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #d0d5dd;
}

.compensacao-aviso {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 10px 12px;
  font-size: 0.84rem;
  line-height: 1.4;
  border: 1px solid;
  border-radius: 10px;
}

.compensacao-aviso .v-icon {
  color: inherit !important;
}

.compensacao-aviso--info {
  color: #1d3fa6;
  background: #eef4ff;
  border-color: #c7d7fe;
}

.compensacao-aviso--warning {
  color: #93370d;
  background: #fffaeb;
  border-color: #fedf89;
}
.compensacao {
  --compensacao-ink: #344054;
  --compensacao-muted: #667085;
  --compensacao-border: #eaecf0;
  --compensacao-primary: #5e72e4;
  padding: 20px;
  color: var(--compensacao-ink);
  background: #ffffff;
  border: 1px solid var(--compensacao-border);
  border-radius: 14px;
}

.compensacao__header {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.compensacao__icone {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(94, 114, 228, 0.1);
  border-radius: 10px;
}

.compensacao__eyebrow {
  color: var(--compensacao-primary);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.compensacao__titulo {
  margin: 2px 0 4px;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
}

.compensacao__descricao {
  margin: 0;
  color: var(--compensacao-muted);
  font-size: 0.86rem;
}

.compensacao-modos {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.compensacao-modo {
  position: relative;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  text-align: left;
  background: #f9fafb;
  border: 1px solid var(--compensacao-border);
  border-radius: 12px;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.compensacao-modo:hover {
  border-color: #c7cdf6;
}

.compensacao-modo--ativo {
  background: #ffffff;
  border-color: var(--compensacao-primary);
  box-shadow: 0 0 0 3px rgba(94, 114, 228, 0.12);
}

.compensacao-modo__texto {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-right: 18px;
}

.compensacao-modo__texto strong {
  font-size: 0.88rem;
}

.compensacao-modo__texto small {
  color: var(--compensacao-muted);
  font-size: 0.76rem;
  line-height: 1.35;
}

.compensacao-modo__check {
  position: absolute !important;
  top: 10px;
  right: 10px;
}

.compensacao-modo:focus-visible,
.compensacao-bloco__toggle:focus-visible,
.compensacao-adicionar:focus-visible {
  outline: 2px solid var(--compensacao-primary);
  outline-offset: 2px;
}

.compensacao__desativada,
.compensacao__explicacao {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  margin: 12px 0 0;
  color: var(--compensacao-muted);
  font-size: 0.82rem;
}

.compensacao-regra {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.compensacao-coluna {
  padding: 14px;
  background: #f9fafb;
  border: 1px solid var(--compensacao-border);
  border-top: 3px solid #12b76a;
  border-radius: 12px;
}

.compensacao-coluna--debito {
  border-top-color: #f04438;
}

.compensacao-coluna__cabecalho {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 10px;
}

.compensacao-coluna__cabecalho div,
.compensacao-ajustes__cabecalho div,
.compensacao-bloco__toggle span {
  display: flex;
  flex-direction: column;
}

.compensacao-coluna__cabecalho small,
.compensacao-ajustes__cabecalho small,
.compensacao-bloco__toggle small {
  color: var(--compensacao-muted);
  font-size: 0.76rem;
}

.compensacao-lista {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.compensacao-item {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid var(--compensacao-border);
  border-radius: 10px;
}

.compensacao-item--inativo {
  opacity: 0.55;
}

.compensacao-item__ordem {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--compensacao-primary);
  font-size: 0.78rem;
  font-weight: 700;
  background: rgba(94, 114, 228, 0.1);
  border-radius: 50%;
}

.compensacao-item__texto {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.compensacao-item__texto strong {
  font-size: 0.86rem;
}

.compensacao-item__texto small {
  overflow: hidden;
  color: var(--compensacao-muted);
  font-size: 0.74rem;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.compensacao-item__acoes {
  display: inline-flex;
  flex: 0 0 auto;
}

.compensacao-lista__vazia {
  padding: 10px;
  color: var(--compensacao-muted);
  font-size: 0.82rem;
  text-align: center;
  border: 1px dashed #d0d5dd;
  border-radius: 10px;
}

.compensacao-disponiveis {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 10px;
  color: var(--compensacao-muted);
  font-size: 0.76rem;
}

.compensacao-adicionar {
  display: inline-flex;
  gap: 2px;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  color: var(--compensacao-ink);
  font-size: 0.78rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px dashed #98a2b3;
  border-radius: 999px;
}

.compensacao-adicionar:hover {
  color: var(--compensacao-primary);
  border-color: var(--compensacao-primary);
}

.compensacao__personalizar {
  margin-top: 12px;
}

.compensacao-bloco {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--compensacao-border);
}

.compensacao-bloco__toggle {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
  text-align: left;
}

.compensacao-bloco__toggle span {
  flex: 1 1 auto;
}

.compensacao-simulador {
  margin-top: 12px;
}

.compensacao-simulador__tabela {
  overflow-x: auto;
}

.compensacao-simulador table {
  width: 100%;
  min-width: 460px;
  border-collapse: collapse;
  font-size: 0.84rem;
}

.compensacao-simulador th,
.compensacao-simulador td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid var(--compensacao-border);
}

.compensacao-simulador thead th {
  color: var(--compensacao-muted);
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.compensacao-simulador tbody th {
  font-weight: 600;
}

.compensacao-simulador tbody th small {
  display: block;
  color: var(--compensacao-muted);
  font-weight: 400;
}

.compensacao-linha--fora {
  color: var(--compensacao-muted);
}

.compensacao-natureza {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  background: #12b76a;
  border-radius: 50%;
}

.compensacao-natureza--debito {
  background: #f04438;
}

.compensacao-hora {
  max-width: 96px;
}

.compensacao-simulador__compensado {
  color: #027a48;
  font-weight: 600;
}

.compensacao-movimentos {
  margin: 12px 0 4px;
  padding: 0;
  list-style: none;
  font-size: 0.84rem;
}

.compensacao-movimentos li {
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 3px 0;
}

.compensacao-ajustes {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.compensacao-ajustes__grupo {
  padding: 14px;
  background: #f9fafb;
  border: 1px solid var(--compensacao-border);
  border-radius: 12px;
}

.compensacao-ajustes__cabecalho {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 4px;
}

.compensacao-ajustes__switch {
  margin-top: 10px;
}

.compensacao-ajustes__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 10px 0 8px;
}

.compensacao-ajustes__chip {
  padding: 3px 10px;
  font-size: 0.76rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid var(--compensacao-border);
  border-radius: 999px;
}

.compensacao-ajustes__nota {
  color: var(--compensacao-muted);
  font-size: 0.76rem;
}

@media (max-width: 960px) {
  .compensacao-modos,
  .compensacao-regra,
  .compensacao-ajustes {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 600px) {
  .compensacao {
    padding: 16px;
  }
}
</style>
